import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import type { Nullable } from '@zen/utils/typescript';

import { useForm } from '../useForm';
import { getContainerScrollTop, getWindowScrollTop } from './helpers';

interface Props {
  additionalYOffset?: number;
}

const ScrollToFirstError: FC<Props> = ({ additionalYOffset = 0 }) => {
  const { errors, isValid, submitCount } = useForm();
  const hasErrors: boolean = Object.keys(errors).length > 0;

  const scrollTo = useCallback(
    (element: Element): void => {
      const scrollableAncestor: Nullable<Element> = element.closest('.js-element-scrollable');

      if (scrollableAncestor) {
        scrollableAncestor.scrollTo?.({
          behavior: 'smooth',
          top: getContainerScrollTop(element, scrollableAncestor, additionalYOffset)
        });

        return;
      }

      window.scrollTo({ behavior: 'smooth', top: getWindowScrollTop(element, additionalYOffset) });
    },
    [additionalYOffset]
  );

  useEffect((): void => {
    if (isValid || !hasErrors || submitCount === 0) {
      return;
    }

    const firstFieldWithError: Nullable<Element> = document.querySelector('.js-field-error');

    if (firstFieldWithError) {
      scrollTo(firstFieldWithError);
    }
  }, [hasErrors, isValid, scrollTo, submitCount]);

  return null;
};

export default ScrollToFirstError;
